import { useState } from 'react'
import { useDetectClickOutside } from 'react-detect-click-outside'
import './style.scss'
import { IEntry } from '../../models/Entry'
import { formatFromUnixMS } from '../../helpers/date'
import { EditIcon } from '../../icons/EditIcon'
import { ThrashIcon } from '../../icons/ThrashIcon'
import { LikeButton } from '../LikeButton'
import { DislikeButton } from '../DislikeButton'
import { EditTextArea } from '../EditTextArea'
import { useAppContext } from '../../contexts/AppContext'
import { AppActionType } from '../../contexts/AppReducer'
import { AttachIcon } from '../../icons/AttachIcon'
import { SmileIcon } from '../../icons/SmileIcon'

export interface EntryProps {
  entry: IEntry
}

export function Entry({ entry }: EntryProps) {
  const { state, dispatch } = useAppContext()

  const [editMode, setEditMode] = useState(false)

  const [preExpandedHeight, setPreExpandedHeight] = useState<number | null>(null)
  const [expanded, setExpanded] = useState(false)

  const [content, setContent] = useState(entry.content)

  const handleEdit = () => {
    setEditMode(true)
    setPreExpandedHeight(entryRef.current?.getBoundingClientRect().height || null)
    setExpanded(true)
  }

  const handleEditCancel = () => {
    setEditMode(false)
  }

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (!editMode && e.propertyName === 'min-height') {
      setExpanded(false)
    }
  }

  const handleUpdate = () => {
    dispatch({
      type: AppActionType.UpdateEntry,
      data: {
        id: entry.id,
        content,
      },
    })
    setEditMode(false)
  }

  const handleLike = () => {
    dispatch({
      type: AppActionType.LikeEntry,
      data: entry.id,
    })
  }

  const handleDislike = () => {
    dispatch({
      type: AppActionType.DislikeEntry,
      data: entry.id,
    })
  }

  const handleDelete = () => {
    dispatch({
      type: AppActionType.RemoveEntry,
      data: entry.id,
    })
  }

  const entryRef = useDetectClickOutside({
    onTriggered: handleEditCancel,
  }) as unknown as React.MutableRefObject<HTMLDivElement>

  return (
    <div
      className='entry'
      style={{
        height: expanded ? preExpandedHeight || 'auto' : 'auto',
        zIndex: expanded ? Date.now() : 1,
      }}
    >
      <div
        ref={entryRef}
        className={`entry__inner ${editMode ? 'entry--edit-mode' : ''}`}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className='entry__body'>
          <img alt='avatar' className='entry__body__avatar' src={entry.user.avatar} />

          <div className='entry__body__content'>
            <div className='entry__body__content__date'>
              {entry.updatedAtUnixMS !== null
                ? `Updated at ${formatFromUnixMS(entry.updatedAtUnixMS)}`
                : formatFromUnixMS(entry.createdAtUnixMS)}
            </div>
            {editMode ? (
              <div className='entry__body__content__edit'>
                <a href='#' className='entry__body__content__username'>
                  {entry.user.username}
                </a>
                <EditTextArea
                  placeholder='Your text here'
                  autoFocus={true}
                  value={content}
                  onChange={setContent}
                />

                <div className='entry__body__content__edit__bottom'>
                  <div className='entry__body__content__edit__bottom__section'>
                    <button className='button-svg' aria-label='attach'>
                      <AttachIcon />
                    </button>

                    <button className='button-svg' aria-label='smile'>
                      <SmileIcon />
                    </button>
                  </div>

                  <div className='entry__body__content__edit__bottom__section'>
                    <button className='button-secondary button-sm' onClick={handleEditCancel}>
                      CANCEL
                    </button>

                    <button className='button-primary button-sm' onClick={handleUpdate}>
                      UPDATE
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className='entry__body__content__text'>
                <a href='#' className='entry__body__content__username'>
                  {entry.user.username}
                </a>
                {entry.content}
              </div>
            )}
          </div>
        </div>

        <div className='entry__footer'>
          <div className='entry__footer__impressions'>
            <LikeButton
              active={state.likedEntryIds.includes(entry.id)}
              value={entry.likeCount}
              onClick={handleLike}
            />
            <DislikeButton
              active={state.dislikedEntryIds.includes(entry.id)}
              value={entry.dislikeCount}
              onClick={handleDislike}
            />
          </div>

          <div className='entry__footer__actions'>
            <button className='button-svg' onClick={handleEdit} aria-label='edit'>
              <EditIcon />
            </button>

            <button className='button-svg' onClick={handleDelete} aria-label='delete'>
              <ThrashIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
